
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Calendar,
  FileText,
  LogOut,
  Mic,
  LineChart,
  Settings,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SECTIONS = [
  {
    label: "Workspace",
    items: [
      {
        to: "/dashboard",
        label: "Executive Overview",
        icon: LayoutDashboard,
      },
      {
        to: "/appointments",
        label: "Appointments",
        icon: Calendar,
      },
      {
        to: "/receptionist",
        label: "AI Voice Receptionist",
        icon: Mic,
      },
      {
        to: "/vendors",
        label: "Vendors & Invoices",
        icon: FileText,
      },
      {
        to: "/finance",
        label: "Finance & Profitability",
        icon: LineChart,
      },
      {
        to: "/business-settings",
        label: "Business Settings",
        icon: Settings,
      },
    ],
  },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const initials = (user?.name || "U")
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <aside
      data-testid="app-sidebar"
      className="
        fixed inset-y-0 left-0 z-40
        flex w-[260px] flex-col
        border-r border-[#DFE7DF]
        text-[#20352D]
      "
      style={{
        background:
          "radial-gradient(ellipse at 0% 12%, rgba(210,230,216,.28), transparent 42%), #F7F8F3",
      }}
    >
      {/* Brand */}
      <div className="flex h-[72px] shrink-0 items-center px-6">
        <div className="mr-3 h-7 w-[4px] rounded-full bg-[#07594B]" />

        <span
          className="text-[20px] font-semibold tracking-[-0.055em] text-[#123F34]"
          style={{ fontFamily: "Geist, sans-serif" }}
        >
          VELQUE
        </span>
      </div>

      {/* Main navigation */}
      <nav
        aria-label="Main navigation"
        className="mt-1 flex-1 overflow-y-auto px-3 pb-3"
      >
        {SECTIONS.map((section) => (
          <div key={section.label} className="mb-4">
            {/* Section label */}
            <div className="mb-2 flex items-center gap-2.5 px-3">
              <span className="h-[6px] w-[6px] shrink-0 rounded-full bg-[#B78B43]" />

              <span className="text-[10px] font-bold uppercase tracking-[0.19em] text-[#62776B]">
                {section.label}
              </span>

              <span className="h-px flex-1 bg-[#DCE5DB]" />
            </div>

            {/* Navigation items */}
            <div className="space-y-0.5">
              {section.items.map(({ to, label, icon: Icon }) => (
                <NavLink
                  key={to}
                  to={to}
                  data-testid={`sidebar-nav-${label
                    .toLowerCase()
                    .replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    [
                      "group relative flex min-h-[38px] items-center gap-2.5",
                      "rounded-lg px-3",
                      "text-[12.5px] font-medium tracking-[-0.01em]",
                      "transition-all duration-200 ease-out",
                      "focus-visible:outline-none focus-visible:ring-2",
                      "focus-visible:ring-[#0B7962] focus-visible:ring-offset-2",
                      isActive
                        ? [
                            "bg-gradient-to-r from-[#07545A] to-[#0B6E74]",
                            "text-white",
                            "shadow-[0_5px_13px_rgba(5,71,58,.16)]",
                          ].join(" ")
                        : [
                            "text-[#50645A]",
                            "hover:bg-[#E7EFE6]",
                            "hover:text-[#07594B]",
                          ].join(" "),
                    ].join(" ")
                  }
                >
                  {({ isActive }) => (
                    <>
                      {/* Gold active indicator */}
                      <span
                        className={[
                          "absolute bottom-[9px] left-0 top-[9px]",
                          "w-[3px] rounded-r-full",
                          isActive ? "bg-[#E5C477]" : "bg-transparent",
                        ].join(" ")}
                        aria-hidden="true"
                      />

                      <Icon
                        size={15}
                        strokeWidth={isActive ? 2 : 1.8}
                        className={[
                          "ml-1 shrink-0 transition-colors duration-200",
                          isActive
                            ? "text-[#E8F5ED]"
                            : "text-[#587366] group-hover:text-[#07594B]",
                        ].join(" ")}
                      />

                      <span className="min-w-0 flex-1 leading-5">
                        {label}
                      </span>

                      {isActive && (
                        <span
                          className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#D9EBDD]"
                          aria-hidden="true"
                        />
                      )}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Account profile and sign out */}
      <div className="shrink-0 border-t border-[#DFE7DF] px-3 pb-3 pt-3">
        <div className="flex items-center gap-2.5 rounded-lg px-2 py-2">
          {user?.picture ? (
            <img
              src={user.picture}
              alt=""
              className="h-8 w-8 shrink-0 rounded-full border border-[#D9E6DA] object-cover"
            />
          ) : (
            <div
              className="
                flex h-8 w-8 shrink-0 items-center justify-center
                rounded-full bg-gradient-to-br from-[#117E70] to-[#06483E]
                text-[11px] font-semibold tracking-wide text-white
                shadow-[0_2px_7px_rgba(6,72,62,.14)]
              "
            >
              {initials}
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="truncate text-[12.5px] font-semibold text-[#263E33]">
              {user?.name || "Your workspace"}
            </div>

            <div className="mt-0.5 truncate text-[11.5px] text-[#68796F]">
              {user?.email}
            </div>
          </div>
        </div>

        <button
          type="button"
          data-testid="sidebar-logout-btn"
          onClick={onLogout}
          className="
            mt-1 flex min-h-[34px] w-full items-center gap-2.5
            rounded-lg px-3 text-[12px] font-medium
            text-[#617368]
            transition-colors duration-200
            hover:bg-[#F0EAE2] hover:text-[#75543A]
            focus-visible:outline-none focus-visible:ring-2
            focus-visible:ring-[#0B7962]
          "
        >
          <LogOut size={14} strokeWidth={1.7} />
          Sign out
        </button>
      </div>
    </aside>
  );
}
